// In theory lottie-react-native has a web implementation that should just work, so this file can
// be deleted. However, we tested it and it didn't work. Worth testing again if we update our versions.
//
// Historic note: we used to use react-native-web-lottie, but this was unmaintained.
import { View } from 'react-native';
import type { LottieViewProps } from 'lottie-react-native';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import lottie, { type AnimationItem } from 'lottie-web';

type LottieViewImperitiveHandle = {
  play: (startFrame?: number, endFrame?: number) => void;
  reset: () => void;
  pause: () => void;
  resume: () => void;
};

/**
 * Web implementation of lottie-react-native's LottieView.
 *
 * Many props are not implemented yet!
 */
const LottieView = forwardRef<LottieViewImperitiveHandle, LottieViewProps>((props, ref) => {
  let speed: number | undefined;
  if (props.duration !== undefined && typeof props.source === 'object' && 'fr' in props.source) {
    // Set the speed from the duration given
    speed = Math.round((props.source.op / props.source.fr) * 1000) / props.duration;
  } else {
    speed = props.speed;
  }

  const viewRef = useRef<View | null>(null);

  // Load animation given in props
  const animation = useRef<AnimationItem | null>(null);
  useEffect(() => {
    animation.current = lottie.loadAnimation({
      // Cast View to HtmlDivElement, since this file is only for web, where they end up being the same.
      container: viewRef.current! as unknown as HTMLDivElement,
      animationData: props.source,
      renderer: 'svg',
      loop: props.loop ?? true,
      autoplay: props.autoPlay ?? false
      // TODO: handle other props when we need them
    });

    if (speed !== undefined) {
      animation.current.setSpeed(speed);
    }

    if (props.onAnimationFinish) {
      // TODO: return true when cancelled
      animation.current.addEventListener('complete', () => props.onAnimationFinish?.(false));
    }

    return () => animation.current?.destroy();
    // Only run on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      play: (startFrame, endFrame) => {
        if (startFrame !== undefined && endFrame !== undefined) {
          animation.current?.playSegments([startFrame, endFrame], true);
        } else {
          animation.current?.play();
        }
      },
      reset: () => {
        animation.current?.stop();
      },
      pause: () => {
        animation.current?.pause();
      },
      resume: () => {
        animation.current?.play();
      }
    }),
    []
  );

  return <View style={props.style} ref={viewRef} />;
});

export default LottieView;
